import React, { useState } from 'react';
import { toast as message } from 'sonner';
import { DateInput3 } from '@blueprintjs/datetime2';
import { enUS } from 'date-fns/locale';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabaseClient } from '../../../utils/supabase';
import type { ActionDialogProps } from '../types';
import EnhancedDialog from '../../EnhancedDialog';

interface MerchantOption {
  id: number;
  business_name: string;
}

export const AchIntakeDialog: React.FC<ActionDialogProps> = ({
  isOpen,
  onClose,
  prefill,
}) => {
  const navigate = useNavigate();

  const [merchantId, setMerchantId] = useState<number | null>(
    prefill?.merchant_id ? Number(prefill.merchant_id) : null
  );
  const [accountHolderName, setAccountHolderName] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountType, setAccountType] = useState<'checking' | 'savings'>('checking');
  const [bankName, setBankName] = useState('');
  const [authorizedAt, setAuthorizedAt] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { data: merchants } = useQuery<MerchantOption[]>({
    queryKey: ['merchants_for_ach_intake'],
    queryFn: async () => {
      const { data, error } = await supabaseClient
        .from('merchant')
        .select('id, business_name')
        .order('business_name');
      if (error) throw error;
      return (data || []) as MerchantOption[];
    },
    staleTime: 30_000,
    enabled: isOpen,
  });

  const { data: stripeMode } = useQuery({
    queryKey: ['ach_provider_config', 'stripe_mode'],
    queryFn: async () => {
      const { data } = await (supabaseClient as any)
        .from('ach_provider_config')
        .select('stripe_mode')
        .single();
      return (data?.stripe_mode as string) ?? 'test';
    },
    staleTime: 30_000,
    enabled: isOpen,
  });

  const handleSubmit = async () => {
    if (!merchantId || !accountHolderName || !routingNumber || !accountNumber || !accountType) return;
    setSubmitting(true);

    try {
      const { data, error } = await supabaseClient.functions.invoke('ach-mandate-intake', {
        body: {
          merchant_id: merchantId,
          account_holder_name: accountHolderName,
          routing_number: routingNumber,
          account_number: accountNumber,
          account_type: accountType,
          bank_name: bankName || undefined,
          authorized_at: authorizedAt || undefined,
          notes: notes || undefined,
        },
      });

      if (data?.error) throw new Error(data.error);
      if (error) throw error;

      message.success(`ACH mandate created (${data.stripe_mode} mode)`);
      resetForm();
      onClose();
      navigate(`/ACH_Mandates/${data.ach_mandate_id}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create ACH mandate';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    if (!prefill?.merchant_id) setMerchantId(null);
    setAccountHolderName('');
    setRoutingNumber('');
    setAccountNumber('');
    setAccountType('checking');
    setBankName('');
    setAuthorizedAt(null);
    setNotes('');
  };

  const handleClose = () => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  };

  const hasPrefillMerchant = !!prefill?.merchant_id;

  return (
    <EnhancedDialog
      isOpen={isOpen}
      onClose={handleClose}
      title="New ACH Mandate"
      style={{ width: 500 }}
      canOutsideClickClose={!submitting}
    >
      <div className="sky-dialog-body">
        <div className={`sky-callout ${stripeMode === 'live' ? 'sky-callout-danger' : 'sky-callout-warning'}`} style={{ marginBottom: 16 }}>
          {stripeMode === 'live'
            ? 'Live Mode — This will create real Stripe objects.'
            : 'Test Mode — Stripe objects will be created in test mode.'}
        </div>

        {!hasPrefillMerchant && (
          <div className="sky-form-group">
            <label className="sky-form-label">Merchant <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(required)</span></label>
            <select
              className="sky-field-input"
              value={merchantId ?? ''}
              onChange={(e) => setMerchantId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Select a merchant...</option>
              {(merchants || []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.business_name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="sky-form-group">
          <label className="sky-form-label">Account Holder Name <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(required)</span></label>
          <input
            className="sky-field-input"
            value={accountHolderName}
            onChange={(e) => setAccountHolderName(e.target.value)}
            placeholder="Name on bank account"
          />
        </div>

        <div className="sky-form-group">
          <label className="sky-form-label">Routing Number <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(required)</span></label>
          <input
            className="sky-field-input"
            value={routingNumber}
            onChange={(e) => setRoutingNumber(e.target.value)}
            placeholder="9-digit ABA routing number"
          />
        </div>

        <div className="sky-form-group">
          <label className="sky-form-label">Account Number <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(required)</span></label>
          <input
            className="sky-field-input"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            placeholder="Bank account number"
          />
        </div>

        <div className="sky-form-group">
          <label className="sky-form-label">Account Type <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(required)</span></label>
          <select
            className="sky-field-input"
            value={accountType}
            onChange={(e) => setAccountType(e.target.value as 'checking' | 'savings')}
          >
            <option value="checking">Checking</option>
            <option value="savings">Savings</option>
          </select>
        </div>

        <div className="sky-form-group">
          <label className="sky-form-label">Bank Name</label>
          <input
            className="sky-field-input"
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            placeholder="Optional"
          />
        </div>

        <div className="sky-form-group">
          <label className="sky-form-label">Mandate Authorized At</label>
          <DateInput3
            locale={enUS}
            value={authorizedAt}
            onChange={(val) => setAuthorizedAt(val)}
            formatDate={(date) => date.toLocaleDateString()}
            parseDate={(str) => new Date(str)}
            placeholder="Date mandate was signed"
          />
        </div>

        <div className="sky-form-group">
          <label className="sky-form-label">Notes</label>
          <textarea
            className="sky-field-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            style={{ resize: 'vertical' }}
          />
        </div>
      </div>

      <div className="sky-dialog-footer">
        <button className="sky-btn sky-btn-ghost" onClick={handleClose} disabled={submitting}>Cancel</button>
        <button
          className="sky-btn sky-btn-primary"
          onClick={handleSubmit}
          disabled={submitting || !merchantId || !accountHolderName || !routingNumber || !accountNumber}
        >
          {submitting ? <span className="sky-spinner" /> : null}
          Create ACH Mandate
        </button>
      </div>
    </EnhancedDialog>
  );
};
